import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { LOGO_URL } from "@/lib/site-data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FILTERS = ["all", "pending", "approved", "rejected"];

export default function AdminReviews() {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("pending");
  const [busy, setBusy] = useState(null);

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/reviews/all`);
      setReviews(data);
    } catch (e) {
      toast.error("Could not load reviews.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const setStatus = async (id, status) => {
    setBusy(id);
    try {
      await axios.put(`${API}/reviews/${id}`, { status });
      setReviews((rs) => rs.map((r) => (r.id === id ? { ...r, status } : r)));
      toast.success(`Review ${status}.`);
    } catch (e) {
      toast.error("Update failed. Please try again.");
    } finally {
      setBusy(null);
    }
  };

  const counts = reviews.reduce(
    (acc, r) => ({ ...acc, [r.status]: (acc[r.status] || 0) + 1 }),
    {}
  );
  const visible =
    filter === "all" ? reviews : reviews.filter((r) => r.status === filter);

  return (
    <main
      className="min-h-screen bg-[var(--bg-primary)] text-[var(--text-primary)] py-16 px-6 md:px-10"
      data-testid="admin-reviews-page"
    >
      <div className="max-w-5xl mx-auto">
        <header className="flex items-center gap-4 mb-12">
          <img src={LOGO_URL} alt="" className="h-12 w-12 object-contain" />
          <div>
            <h1 className="font-display text-3xl md:text-4xl">Review Manager</h1>
            <p className="font-body text-[11px] uppercase tracking-[0.3em] text-[var(--text-secondary)] mt-1">
              Approve or reject visitor reviews
            </p>
          </div>
          <a
            href="/"
            className="btn-outline ml-auto hidden sm:inline-flex"
            data-testid="admin-back-to-site"
          >
            View Site
          </a>
        </header>

        <div className="flex flex-wrap gap-2 mb-10" data-testid="admin-filter-tabs">
          {FILTERS.map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              data-testid={`admin-filter-${f}`}
              className={`px-4 py-2 font-body text-[11px] uppercase tracking-[0.2em] border transition-colors duration-300 ${
                filter === f
                  ? "border-[var(--accent)] text-[var(--accent)] bg-[var(--accent)]/10"
                  : "border-white/15 text-[var(--text-secondary)] hover:border-white/40"
              }`}
            >
              {f}
              {f !== "all" && (
                <span className="ml-2 opacity-70">{counts[f] || 0}</span>
              )}
            </button>
          ))}
        </div>

        {loading ? (
          <p
            className="font-body text-xs uppercase tracking-widest text-[var(--text-secondary)]"
            data-testid="admin-loading"
          >
            Loading reviews…
          </p>
        ) : visible.length === 0 ? (
          <div
            className="border border-white/10 py-16 text-center"
            data-testid="admin-empty"
          >
            <p className="font-display italic text-2xl">
              No {filter === "all" ? "" : filter} reviews.
            </p>
          </div>
        ) : (
          <div className="flex flex-col gap-4">
            {visible.map((r) => (
              <article
                key={r.id}
                className="border border-white/10 bg-[var(--bg-secondary)] p-6 md:p-8"
                data-testid={`admin-review-${r.id}`}
              >
                <div className="flex flex-wrap items-start justify-between gap-4 mb-4">
                  <div>
                    <div className="flex items-center gap-3 mb-1">
                      <span className="font-display text-lg">{r.name}</span>
                      <StatusBadge status={r.status} />
                    </div>
                    <div className="font-body text-[10px] uppercase tracking-[0.25em] text-[var(--text-secondary)]">
                      {[r.role, r.email, new Date(r.created_at).toLocaleDateString()]
                        .filter(Boolean)
                        .join(" · ")}
                    </div>
                  </div>
                  <Stars value={r.rating} />
                </div>
                <blockquote className="font-display text-lg md:text-xl leading-snug mb-6">
                  "{r.quote}"
                </blockquote>
                <div className="flex flex-wrap gap-3">
                  {r.status !== "approved" && (
                    <button
                      onClick={() => setStatus(r.id, "approved")}
                      disabled={busy === r.id}
                      className="btn-solid"
                      data-testid={`admin-approve-${r.id}`}
                    >
                      Approve
                    </button>
                  )}
                  {r.status !== "rejected" && (
                    <button
                      onClick={() => setStatus(r.id, "rejected")}
                      disabled={busy === r.id}
                      className="btn-outline"
                      style={{ borderColor: "rgba(248,113,113,0.5)", color: "#f8a3a3" }}
                      data-testid={`admin-reject-${r.id}`}
                    >
                      Reject
                    </button>
                  )}
                  {r.status !== "pending" && (
                    <button
                      onClick={() => setStatus(r.id, "pending")}
                      disabled={busy === r.id}
                      className="btn-outline"
                      data-testid={`admin-pending-${r.id}`}
                    >
                      Move to Pending
                    </button>
                  )}
                </div>
              </article>
            ))}
          </div>
        )}
      </div>
    </main>
  );
}

function StatusBadge({ status }) {
  const styles = {
    approved: "border-emerald-400/50 text-emerald-300",
    pending: "border-[var(--accent)]/50 text-[var(--accent)]",
    rejected: "border-red-400/50 text-red-300",
  };
  return (
    <span
      className={`px-2 py-0.5 border font-body text-[9px] uppercase tracking-[0.2em] ${styles[status]}`}
    >
      {status}
    </span>
  );
}

function Stars({ value = 0 }) {
  return (
    <div className="flex items-center gap-1">
      {[1, 2, 3, 4, 5].map((n) => (
        <svg
          key={n}
          width="15"
          height="15"
          viewBox="0 0 24 24"
          fill={n <= value ? "var(--accent)" : "none"}
          stroke="var(--accent)"
          strokeWidth="1.5"
        >
          <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
        </svg>
      ))}
    </div>
  );
}
